import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getAppraisalResponseAPI } from "api/apiHelper";
import { AppraisalResponse } from "types/appraisal-response-types";
import Card from "components/Card";
import Backdrop from "components/Backdrop";
import ErrorBox from "components/ErrorBox";
import Button from "components/Button";
import { toast } from "react-toastify";
import { MdArrowBack, MdEdit } from "react-icons/md";

const ViewAppraisal = () => {
  const { responseId } = useParams<{ responseId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [response, setResponse] = useState<AppraisalResponse | null>(null);

  useEffect(() => {
    if (responseId) {
      fetchResponse();
    }
  }, [responseId]);

  const fetchResponse = async () => {
    try {
      setLoading(true);
      setError(null);
      if (!responseId) {
        setError("Response ID is required");
        return;
      }
      const apiResponse = await getAppraisalResponseAPI(Number(responseId));
      if (apiResponse.data) {
        setResponse(apiResponse.data);
      } else {
        setError("Response data not found");
      }
    } catch (err: any) {
      const errorMessage = err?.response?.data?.error || err?.response?.data?.detail || err.message || "Failed to load appraisal response.";
      setError(errorMessage);
      console.error("Error fetching appraisal response:", err);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const calculateScore = () => {
    if (!response) return 0;
    
    let totalScore = 0;
    response.question_responses.forEach((qr) => {
      if (qr.question_weightage) {
        const questionScore = (qr.rating / 5) * qr.question_weightage;
        totalScore += questionScore;
      }
    });
    
    return totalScore.toFixed(2);
  };

  if (loading) {
    return <Backdrop show={true} />;
  }

  if (error && !response) {
    return (
      <div className="mt-5">
        <Card extra="p-6">
          <ErrorBox errorMessage={error || "Appraisal response not found"} />
          <div className="mt-4 flex justify-end">
            <Button
              type="button"
              varient="secondary"
              onClick={() => navigate('/client-appraisals')}
            >
              Back to Appraisals
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  if (!response) {
    return (
      <div className="mt-5">
        <Card extra="p-6">
          <ErrorBox errorMessage="Appraisal response not found" />
          <div className="mt-4 flex justify-end">
            <Button
              type="button"
              varient="secondary"
              onClick={() => navigate('/client-appraisals')}
            >
              Back to Appraisals
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="mt-5">
      <div className="mb-6 flex items-center justify-between">
        <Button
          varient="secondary"
          onClick={() => navigate('/client-appraisals')}
          className="flex items-center gap-2 px-4 py-2"
        >
          <MdArrowBack className="h-5 w-5" />
          Back
        </Button>
        <Button
          varient="primary"
          onClick={() => navigate(`/client-appraisals/${response.cycle.id}/employee/${response.employee.id}`)}
          className="flex items-center gap-2 px-6 py-2"
        >
          <MdEdit className="h-5 w-5" />
          Edit Response
        </Button>
      </div>

      <Card extra="p-6">
        <div className="mb-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-2xl font-bold text-navy-700 dark:text-white mb-2">
                {response.cycle.name}
              </h2>
              {response.cycle.description && (
                <p className="text-gray-600 dark:text-gray-400 mb-2">
                  {response.cycle.description}
                </p>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mt-4">
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Employee: </span>
                  <span className="text-navy-700 dark:text-white font-semibold">
                    {response.employee?.full_name || `${response.employee?.first_name || ''} ${response.employee?.last_name || ''}`.trim() || 'Unknown'}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Status: </span>
                  <span className={`px-2 py-1 text-xs font-semibold rounded ${
                    response.status === 'SUBMITTED'
                      ? 'text-green-600 bg-green-100 dark:bg-green-900/20'
                      : 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/20'
                  }`}>
                    {response.status}
                  </span>
                </div>
                {response.submitted_at && (
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Submitted on: </span>
                    <span className="text-navy-700 dark:text-white font-medium">
                      {formatDate(response.submitted_at)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="mb-6 p-4 bg-brand-50 dark:bg-brand-900/20 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Overall Score</p>
                <p className="text-3xl font-bold text-brand-600 dark:text-brand-400">
                  {calculateScore()}%
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {response.question_responses.map((qr, index) => (
            <div
              key={qr.id || index}
              className="bg-gray-50 dark:bg-navy-700/50 rounded-lg p-4"
            >
              <div className="mb-4">
                <label className="block text-sm font-medium text-navy-700 dark:text-white mb-1">
                  Question #{index + 1} ({qr.question_weightage}% weightage)
                </label>
                <p className="text-navy-700 dark:text-white font-medium">
                  {qr.question_text}
                </p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-navy-700 dark:text-white mb-3">
                  Rating
                </label>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-3">
                  {[1, 2, 3, 4, 5].map((rating) => {
                    const isSelected = qr.rating === rating;
                    const optionText = qr.question_options?.[rating.toString()] || 'N/A';
                    return (
                      <div
                        key={rating}
                        className={`
                          p-4 rounded-xl border-2 transition-all
                          ${isSelected
                            ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/20 ring-2 ring-brand-200 dark:ring-brand-800'
                            : 'border-gray-200 dark:border-navy-600 bg-white dark:bg-navy-700'
                          }
                        `}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <div className={`
                            w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm
                            ${isSelected
                              ? 'bg-brand-500 text-white'
                              : 'bg-gray-200 dark:bg-navy-600 text-gray-600 dark:text-gray-300'
                            }
                          `}>
                            {rating}
                          </div>
                          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                            / 5
                          </span>
                        </div>
                        <p className={`text-sm font-medium mt-2 ${
                          isSelected
                            ? 'text-brand-700 dark:text-brand-300'
                            : 'text-navy-700 dark:text-white'
                        }`}>
                          {optionText}
                        </p>
                        {isSelected && (
                          <div className="mt-2 flex items-center gap-1">
                            <i className="fas fa-check-circle text-brand-500 text-xs"></i>
                            <span className="text-xs text-brand-600 dark:text-brand-400 font-semibold">
                              Selected
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                  Score: {qr.question_weightage ? ((qr.rating / 5) * qr.question_weightage).toFixed(2) : '0'}%
                </div>
              </div>

              {qr.comment && (
                <div>
                  <label className="block text-sm font-medium text-navy-700 dark:text-white mb-2">
                    Comment
                  </label>
                  <p className="text-navy-700 dark:text-white bg-white dark:bg-navy-800 p-3 rounded-lg border border-gray-200 dark:border-navy-700">
                    {qr.comment}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

export default ViewAppraisal;
